import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, User, Phone, Mail, MapPin, Stamp, Calendar,
  CreditCard, Banknote, Send, FileText, Building2, Check, Upload, X,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { PricingModal } from "@/pages/workspace/ApplicationDetail";
import { fmtNum } from "@/lib/format";

/**
 * Office Quick Intake — for walk-in / WhatsApp clients.
 *
 * Philosophy: the client is sitting across from the employee. The form is
 * radio-based (not free-form typing) for speed (<3 min target).
 *
 * Wire format → POST /api/workspace/applications/create-on-behalf
 *   multipart/form-data:
 *     payload_json = JSON.stringify({ ...clientFields, members:[], payment:{...} })
 *     files[]      = passport / ID / contract attachments
 *     file_types[] = matching labels
 */

const APPLICANT_TYPES = [
  { code: "citizen",  label_ar: "مواطن سعودي",   label_en: "Saudi citizen",  doc_default: "national_id" },
  { code: "resident", label_ar: "مقيم",          label_en: "Resident",       doc_default: "iqama" },
  { code: "family",   label_ar: "عائلة",         label_en: "Family",         doc_default: "family_card" },
  { code: "individual", label_ar: "فرد",         label_en: "Individual",     doc_default: "passport" },
];

const ATTACHED_DOCS = [
  { code: "passport",      label_ar: "جواز سفر",   label_en: "Passport" },
  { code: "national_id",   label_ar: "هوية وطنية", label_en: "National ID" },
  { code: "iqama",         label_ar: "إقامة",      label_en: "Iqama" },
  { code: "family_card",   label_ar: "كرت عائلة", label_en: "Family card" },
  { code: "marriage_contract", label_ar: "عقد زواج", label_en: "Marriage contract" },
];

const COUNTRIES = [
  { code: "schengen",   label_ar: "شنغن",        label_en: "Schengen" },
  { code: "uk",         label_ar: "المملكة المتحدة", label_en: "United Kingdom" },
  { code: "usa",        label_ar: "أمريكا",      label_en: "USA" },
  { code: "canada",     label_ar: "كندا",        label_en: "Canada" },
  { code: "russia",     label_ar: "روسيا",       label_en: "Russia" },
  { code: "india",      label_ar: "الهند",       label_en: "India" },
  { code: "turkey",     label_ar: "تركيا",       label_en: "Turkey" },
  { code: "egypt",      label_ar: "مصر",         label_en: "Egypt" },
  { code: "other",      label_ar: "أخرى",        label_en: "Other" },
];

const VISA_TYPES = [
  { code: "tourism",   label_ar: "سياحة",       label_en: "Tourism" },
  { code: "business",  label_ar: "عمل",         label_en: "Business" },
  { code: "study",     label_ar: "دراسة",       label_en: "Study" },
  { code: "visit",     label_ar: "زيارة",       label_en: "Visit" },
  { code: "transit",   label_ar: "ترانزيت",     label_en: "Transit" },
  { code: "other",     label_ar: "أخرى",        label_en: "Other" },
];

const PAYMENT_METHODS = [
  { code: "cash",     label_ar: "نقد",   label_en: "Cash",       icon: Banknote },
  { code: "pos",      label_ar: "شبكة",  label_en: "POS card",   icon: CreditCard },
  { code: "transfer", label_ar: "تحويل", label_en: "Transfer",   icon: Send },
];

const CARD_BRANDS = [
  { code: "mada",       label_ar: "مدى",         label_en: "Mada" },
  { code: "visa",       label_ar: "فيزا",        label_en: "Visa" },
  { code: "mastercard", label_ar: "ماستركارد",   label_en: "Mastercard" },
  { code: "other",      label_ar: "أخرى",        label_en: "Other" },
];

export default function OfficeSubmit() {
  const { theme, dir, lang } = useI18n();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    // Client minimal info
    client_full_name: "",
    client_mobile_number: "",
    client_email: "",
    // Sponsor / employer + Saudi national address (both optional, but useful
    // for embassies that ask for proof of residence / income).
    sponsor_name: "",
    sponsor_type: "individual",
    national_address: "",
    // Trip
    applicant_type: "individual",
    country: "schengen",
    country_other: "",
    visa_type: "tourism",
    visa_type_other: "",
    travel_date: "",
    // Attached docs (quick pick — actual files attached separately)
    attached_doc: "passport",
    // Appointment (biometric / consulate) — the employee books it directly
    // and optionally attaches proof (email / PDF / screenshot). Kept
    // separate from `travel_date` on purpose: travel_date is the trip,
    // appointment_date is when the client presents at the embassy.
    appointment_date: "",
    appointment_time: "",
    // Payment
    payment_amount: "",
    payment_method: "cash",
    card_brand: "mada",
    pos_reference: "",
    // Official entity
    official_entity: "",
  });
  const [files, setFiles] = useState([]); // [{ file, label }]
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null); // { application_number, id }
  // Open the same PricingModal manager uses, inline on this page, right
  // after the application is created — no navigation.
  const [pricingOpen, setPricingOpen] = useState(false);
  // Wizard step (1 → 4): Client → Trip → Entity → Docs & Save.
  // Purely visual: does NOT change form logic or submit payload.
  const [step, setStep] = useState(1);
  const totalSteps = 4;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const T = lang === "ar" ? {
    title: "إدخال داخلي سريع",
    sub: "للعملاء الحاضرين في المكتب (واتساب / حضور). يستهدف ≤ 3 دقائق.",
    back: "العودة",
    section_client: "بيانات العميل",
    section_trip: "بيانات الرحلة",
    section_payment: "تحصيل الدفع",
    payment_hint: "اختياري — اتركها فارغة لو ما حصّلت بعد. فور حفظ المعاملة يُفتح مودال «إعداد الفاتورة» تلقائياً (نفس الشاشة التي تستخدمها الإدارة) ثم تختار طريقة الدفع.",
    optional: "اختياري",
    section_pricing: "تفاصيل الفاتورة (التسعير)",
    pricing_empty: "لا توجد بنود — اختر من الكتالوج أو أضف بنداً مخصصاً.",
    pricing_add_custom: "بند مخصص",
    pricing_qty: "العدد",
    pricing_unit: "السعر",
    pricing_subtotal: "المجموع الفرعي",
    pricing_total: "الإجمالي",
    pricing_item_ph: "اسم البند",
    pricing_office_rev: "إيراد المكتب",
    pricing_passthrough: "مرور (لا يحتسب لعمولة)",
    pricing_from_catalog: "إضافة من الكتالوج",
    pricing_auto_amount: "المبلغ يُحسب تلقائياً من البنود",
    pricing_amount_override: "تعديل يدوي",
    pricing_amount_reset: "استرجاع المجموع تلقائياً",
    section_docs: "مستندات المعاملة",
    docs_hint: "ارفع أي مستند مهم (جواز، هوية، خطاب دعوة، إيصال شبكة، صورة الحوالة، ..) واكتب اسماً واضحاً لكل مستند. جميع المرفقات اختيارية لكن مفيدة للأرشيف.",
    doc_title_ph: "اكتب اسم المستند (مثال: جواز الأب / إيصال الشبكة)",
    add_appointment_doc: "إرفاق مستند الموعد",
    section_appointment: "الموعد الرسمي",
    appointment_hint: "متى يحضر العميل عند الجهة الرسمية؟ يمكنك تسجيل التاريخ والوقت وإرفاق تأكيد الموعد (اختياري).",
    appointment_date: "تاريخ الموعد",
    appointment_time: "وقت الموعد",
    section_entity: "الجهة الرسمية",
    name: "الاسم الكامل",
    mobile: "رقم الجوال",
    email: "البريد الإلكتروني (اختياري)",
    applicant: "نوع المتقدم",
    attached_doc: "المستند المرفق",
    country: "الدولة المقصودة",
    country_other_ph: "اكتب الدولة…",
    visa_type: "نوع التأشيرة",
    visa_other_ph: "اكتب النوع…",
    travel_date: "تاريخ السفر",
    amount: "المبلغ المُحصَّل (ر.س)",
    method: "طريقة الدفع",
    pos_ref: "مرجع البطاقة (Reference)",
    pos_ref_ph: "مثال: 1234567",
    pos_required: "إلزامي عند الدفع بالشبكة",
    entity: "السفارة / المركز الرسمي",
    entity_ph: "مثال: قنصلية إسبانيا - الرياض",
    upload: "رفع إيصال سداد الموعد / مستند المعاملة",
    submit: "حفظ وإصدار رقم معاملة",
    submitting: "جاري الحفظ…",
    success_title: "تم إصدار رقم المعاملة",
    success_msg: "أرسل رقم المعاملة للعميل لمتابعة حالته.",
    open_app: "فتح المعاملة",
    open_pricing_now: "إعداد الفاتورة الآن",
    new_app: "معاملة جديدة",
    err_required: "أكمل الحقول الإلزامية (الاسم، الجوال، المبلغ).",
    err_name_short: "اسم العميل يجب أن يحتوي على حرفين على الأقل.",
    err_mobile_short: "رقم الجوال يجب أن يحتوي على 4 أرقام على الأقل.",
  } : {
    title: "Office Quick Intake",
    sub: "For walk-in / WhatsApp clients. Target ≤ 3 minutes.",
    back: "Back",
    section_client: "Client info",
    section_trip: "Trip info",
    section_payment: "Payment collection",
    payment_hint: "Optional — leave blank if not collected yet. As soon as the application is saved, the SAME invoice modal Abdulaziz uses opens automatically. You'll set line items there and choose how the client paid.",
    optional: "optional",
    section_pricing: "Invoice line items",
    pricing_empty: "No items — pick from the catalog or add a custom line.",
    pricing_add_custom: "Custom line",
    pricing_qty: "Qty",
    pricing_unit: "Price",
    pricing_subtotal: "Subtotal",
    pricing_total: "Total",
    pricing_item_ph: "Item name",
    pricing_office_rev: "Office revenue",
    pricing_passthrough: "Pass-through (no commission)",
    pricing_from_catalog: "Add from catalog",
    pricing_auto_amount: "Amount auto-calculated from items",
    pricing_amount_override: "Override manually",
    pricing_amount_reset: "Restore auto total",
    section_docs: "Application documents",
    docs_hint: "Upload anything relevant (passport, ID, invitation letter, POS receipt, transfer screenshot, ..) and give each file a clear title. All optional but useful for the archive.",
    doc_title_ph: "Document title (e.g. Father's passport / POS receipt)",
    add_appointment_doc: "Attach appointment document",
    section_appointment: "Official appointment",
    appointment_hint: "When does the client present at the embassy / center? Record date + time and optionally attach the confirmation.",
    appointment_date: "Appointment date",
    appointment_time: "Appointment time",
    section_entity: "Official entity",
    name: "Full name",
    mobile: "Mobile",
    email: "Email (optional)",
    applicant: "Applicant type",
    attached_doc: "Attached document",
    country: "Destination",
    country_other_ph: "Type country…",
    visa_type: "Visa type",
    visa_other_ph: "Type visa…",
    travel_date: "Travel date",
    amount: "Amount collected (SAR)",
    method: "Payment method",
    pos_ref: "POS reference",
    pos_ref_ph: "e.g. 1234567",
    pos_required: "Required for POS payments",
    entity: "Embassy / official center",
    entity_ph: "e.g. Spanish Consulate — Riyadh",
    upload: "Upload appointment receipt / transaction document",
    submit: "Save & issue tracking number",
    submitting: "Saving…",
    success_title: "Tracking number issued",
    success_msg: "Share the number with the client for follow-up.",
    open_app: "Open application",
    open_pricing_now: "Set up invoice now",
    new_app: "New application",
    err_required: "Complete required fields (name, mobile, amount).",
    err_name_short: "Client name must be at least 2 characters.",
    err_mobile_short: "Mobile number must be at least 4 digits.",
  };

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles((cur) => [
      ...cur,
      ...list.map((f) => ({
        file: f,
        // Default label = filename without extension, editable by employee.
        label: (f.name || "document").replace(/\.[^.]+$/, "").slice(0, 80),
      })),
    ]);
    e.target.value = "";
  };

  const appointmentFileRef = useRef(null);
  const onAppointmentFiles = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles((cur) => [
      ...cur,
      ...list.map((f) => ({ file: f, label: lang === "ar" ? "الموعد الرسمي" : "Official appointment" })),
    ]);
    e.target.value = "";
  };

  const relabelFile = (idx, newLabel) => {
    setFiles((cur) => cur.map((f, i) => (i === idx ? { ...f, label: newLabel } : f)));
  };

  const removeFile = (idx) => setFiles((cur) => cur.filter((_, i) => i !== idx));

  const submit = async () => {
    setErr("");
    const name = form.client_full_name.trim();
    const mobile = form.client_mobile_number.trim();
    if (!name || !mobile) {
      setErr(T.err_required);
      return;
    }
    // Same rules as backend PortalSubmitIn (min-length 2 name, 4 mobile) —
    // caught client-side so employees get a friendly Arabic message instead
    // of Pydantic's technical `string_too_short` dump.
    if (name.length < 2) { setErr(T.err_name_short); return; }
    if (mobile.length < 4) { setErr(T.err_mobile_short); return; }
    setBusy(true);
    try {
      const country = form.country === "other" ? (form.country_other || "Other") : form.country;
      const visa = form.visa_type === "other" ? (form.visa_type_other || "Other") : form.visa_type;

      // Payment is now collected EXCLUSIVELY through the PricingModal that
      // auto-opens after creation — we never send a `payment` block from
      // this form anymore. The flow is: create app → set fees → record
      // payment → ledger entry, all driven from the unified modal.
      const payload = {
        client_full_name: form.client_full_name.trim(),
        client_mobile_number: form.client_mobile_number.trim(),
        client_email: form.client_email.trim() || null,
        sponsor_name: (form.sponsor_name || "").trim() || null,
        sponsor_type: form.sponsor_type || null,
        national_address: (form.national_address || "").trim() || null,
        nationality_type: form.applicant_type === "resident" ? "non_saudi" : "saudi",
        marital_status: form.applicant_type === "family" ? "married" : "single",
        applicant_type: form.applicant_type === "citizen" || form.applicant_type === "resident"
          ? "individual" : form.applicant_type,
        country,
        visa_type: visa,
        travel_date: form.travel_date || null,
        // Appointment metadata — accepted by the create-on-behalf endpoint
        // as an out-of-band block and mirrored onto the application doc so
        // it appears immediately in the client's tracking portal.
        appointment_date: form.appointment_date || null,
        appointment_time: form.appointment_time || null,
        appointment_official_entity: (form.official_entity || "").trim() || null,
        members: [],
        translation_service: false,
        muaqqib_service: false,
        muaqqib_persons_count: 0,
      };

      const fd = new FormData();
      fd.append("payload_json", JSON.stringify(payload));
      files.forEach(({ file, label }) => {
        fd.append("files", file);
        fd.append("file_types", label);
      });

      const { data } = await api.post("/workspace/applications/create-on-behalf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(data);
      // Auto-open the SAME PricingModal Abdulaziz uses, in-place — the
      // employee finishes the invoice immediately without leaving the page.
      setPricingOpen(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // ────────── Render success state ──────────
  if (success) {
    return (
      <div dir={dir} style={{ minHeight: "calc(100vh - 120px)", background: theme.bg, color: theme.text, padding: "40px 16px" }} data-testid="office-submit-success">
        <div style={{ maxWidth: 520, margin: "0 auto", textAlign: "center" }}>
          <div style={{
            width: 80, height: 80, borderRadius: "50%", background: theme.gold,
            margin: "0 auto 20px", display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Check size={42} strokeWidth={3} color="#0a0a0a" />
          </div>
          <h1 style={{ color: theme.gold, fontSize: 26, fontWeight: 900, margin: "0 0 6px" }}>{T.success_title}</h1>
          <p style={{ color: theme.textMuted, fontSize: 14, margin: "0 0 20px" }}>{T.success_msg}</p>
          <div data-testid="success-app-number" style={{
            background: theme.surface, border: `2px dashed ${theme.gold}`,
            padding: "20px 24px", borderRadius: 14, fontSize: 24, fontWeight: 900,
            letterSpacing: 1.2, color: theme.gold, marginBottom: 24, direction: "ltr",
          }}>
            {success.application_number}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button data-testid="open-pricing-btn"
              onClick={() => setPricingOpen(true)}
              style={{
                background: theme.gold, color: "#0a0a0a", border: "none",
                padding: "10px 22px", borderRadius: 10, fontWeight: 800, fontSize: 14,
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
              }}>
              <FileText size={16} strokeWidth={2.5} /> {T.open_pricing_now || "إعداد الفاتورة الآن"}
            </button>
            <button data-testid="open-application-btn" onClick={() => navigate(`/applications/${success.id}`)}
              style={{ background: "transparent", color: theme.text, border: `1px solid ${theme.gold}`, padding: "10px 22px", borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
              {T.open_app}
            </button>
            <button data-testid="new-application-btn" onClick={() => { setSuccess(null); setPricingOpen(false); setForm({ ...form, client_full_name: "", client_mobile_number: "", client_email: "", payment_amount: "", pos_reference: "", official_entity: "" }); setFiles([]); }}
              style={{ background: "transparent", color: theme.text, border: `1px solid ${theme.border}`, padding: "10px 22px", borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
              {T.new_app}
            </button>
          </div>
        </div>
        {/* SAME PricingModal Abdulaziz uses, embedded on this page so the
            employee finishes the invoice inline without page navigation. */}
        {pricingOpen && success?.id && (
          <PricingModal
            appId={success.id}
            theme={theme}
            onClose={() => setPricingOpen(false)}
            onDone={() => { setPricingOpen(false); }}
          />
        )}
      </div>
    );
  }

  // ────────── Render form ──────────
  const css = makeStyles(theme, dir);

  const Pill = ({ active, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid} style={{
      padding: "9px 16px", borderRadius: 999, fontSize: 13, fontWeight: 700,
      border: `1.5px solid ${active ? theme.gold : theme.border}`,
      background: active ? theme.gold : "transparent",
      color: active ? "#0a0a0a" : theme.text,
      cursor: "pointer", transition: "all .15s",
    }}>{children}</button>
  );

  return (
    <div dir={dir} style={{ minHeight: "calc(100vh - 120px)", background: theme.bg, color: theme.text, padding: "12px 0 60px" }} data-testid="office-submit-page">
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "12px 16px 0" }}>
        <button onClick={() => navigate("/applications")} style={css.backBtn} data-testid="office-submit-back">
          <ArrowRight size={13} strokeWidth={2.4} style={{ transform: dir === "rtl" ? "none" : "rotate(180deg)" }} />
          {T.back}
        </button>
        <h1 style={css.h1}><Building2 size={22} strokeWidth={2.6} style={{ verticalAlign: "middle" }} /> {T.title}</h1>
        <p style={css.sub}>{T.sub}</p>

        {/* ─── Step indicator ─── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "12px 0", marginBottom: 4,
        }} data-testid="office-submit-steps">
          <span style={{ fontSize: 12, fontWeight: 800, color: theme.gold, minWidth: 88 }}>
            {lang === "ar" ? `الخطوة ${step} من ${totalSteps}` : `Step ${step} of ${totalSteps}`}
          </span>
          <div style={{ flex: 1, display: "flex", gap: 4 }}>
            {Array.from({ length: totalSteps }, (_, i) => (
              <span key={i} style={{
                flex: 1, height: 4, borderRadius: 2,
                background: i < step ? theme.gold : "rgba(255,255,255,0.12)",
                transition: "background 150ms",
              }} />
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 780, margin: "0 auto", padding: "0 16px" }}>
        {err && <div style={css.err} data-testid="office-form-error">{err}</div>}

        {/* ── Client ── */}
        {step === 1 && (
        <section style={css.card}>
          <h2 style={css.h2}><User size={16} /> {T.section_client}</h2>
          <div style={css.grid2}>
            <Input value={form.client_full_name} onChange={(v) => upd("client_full_name", v)} placeholder={T.name} testid="of-name" icon={User} />
            <Input value={form.client_mobile_number} onChange={(v) => upd("client_mobile_number", v)} placeholder={T.mobile} testid="of-mobile" icon={Phone} inputMode="tel" />
            <Input value={form.client_email} onChange={(v) => upd("client_email", v)} placeholder={T.email} testid="of-email" icon={Mail} />
          </div>

          {/* Sponsor / employer — small inline row (optional). The employee
              can leave this blank if the client doesn't have one. */}
          <Label>{lang === "ar" ? "الكفيل / جهة العمل (اختياري)" : "Sponsor / Employer (optional)"}</Label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select
              value={form.sponsor_type}
              onChange={(e) => upd("sponsor_type", e.target.value)}
              style={{ ...css.input, flex: "0 0 140px" }}
              data-testid="of-sponsor-type"
            >
              <option value="individual">{lang === "ar" ? "كفيل (شخص)" : "Personal"}</option>
              <option value="company">{lang === "ar" ? "شركة / جهة عمل" : "Company / Employer"}</option>
            </select>
            <Input
              value={form.sponsor_name}
              onChange={(v) => upd("sponsor_name", v)}
              placeholder={lang === "ar" ? "اسم الكفيل أو الشركة" : "Sponsor / Company name"}
              testid="of-sponsor-name"
              icon={Building2}
              style={{ flex: 1, minWidth: 200 }}
            />
          </div>

          {/* Saudi national address — optional, single free-text line. */}
          <Label>{lang === "ar" ? "العنوان الوطني (اختياري)" : "National Address (optional)"}</Label>
          <Input
            value={form.national_address}
            onChange={(v) => upd("national_address", v)}
            placeholder={lang === "ar" ? "1234 - حي النخيل، الرياض 12345" : "1234 - Al Nakheel, Riyadh 12345"}
            testid="of-national-address"
            icon={MapPin}
          />

          <Label>{T.applicant}</Label>
          <div style={css.pillRow} data-testid="of-applicant-row">
            {APPLICANT_TYPES.map((a) => (
              <Pill key={a.code} active={form.applicant_type === a.code}
                onClick={() => upd("applicant_type", a.code)} testid={`of-applicant-${a.code}`}>
                {lang === "ar" ? a.label_ar : a.label_en}
              </Pill>
            ))}
          </div>
          {/* NOTE: the old fixed "attached document type" pill row was
              removed — the employee now names each attachment freely in
              Step 4 (Documents). */}
        </section>
        )}

        {/* ── Trip ── */}
        {step === 2 && (
        <section style={css.card}>
          <h2 style={css.h2}><Stamp size={16} /> {T.section_trip}</h2>
          <Label>{T.country}</Label>
          <div style={css.pillRow} data-testid="of-country-row">
            {COUNTRIES.map((c) => (
              <Pill key={c.code} active={form.country === c.code} onClick={() => upd("country", c.code)} testid={`of-country-${c.code}`}>
                {lang === "ar" ? c.label_ar : c.label_en}
              </Pill>
            ))}
          </div>
          {form.country === "other" && (
            <Input value={form.country_other} onChange={(v) => upd("country_other", v)} placeholder={T.country_other_ph} testid="of-country-other" icon={MapPin} />
          )}

          <Label>{T.visa_type}</Label>
          <div style={css.pillRow} data-testid="of-visa-row">
            {VISA_TYPES.map((v) => (
              <Pill key={v.code} active={form.visa_type === v.code} onClick={() => upd("visa_type", v.code)} testid={`of-visa-${v.code}`}>
                {lang === "ar" ? v.label_ar : v.label_en}
              </Pill>
            ))}
          </div>
          {form.visa_type === "other" && (
            <Input value={form.visa_type_other} onChange={(v) => upd("visa_type_other", v)} placeholder={T.visa_other_ph} testid="of-visa-other" icon={Stamp} />
          )}

          <Label>{T.travel_date}</Label>
          <Input type="date" value={form.travel_date} onChange={(v) => upd("travel_date", v)} testid="of-travel-date" icon={Calendar} />
        </section>
        )}

        {/* ── Official entity + Appointment ── */}
        {step === 3 && (
        <section style={css.card}>
          <h2 style={css.h2}><Building2 size={16} /> {T.section_entity}</h2>
          <Input value={form.official_entity} onChange={(v) => upd("official_entity", v)} placeholder={T.entity_ph} testid="of-entity" icon={Building2} />

          <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px dashed ${theme.border}` }}>
            <h2 style={css.h2}><Calendar size={16} /> {T.section_appointment}</h2>
            <p style={{ fontSize: 12, color: theme.textMuted, margin: "0 0 10px", lineHeight: 1.7 }}>
              {T.appointment_hint}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <Label>{T.appointment_date}</Label>
                <Input
                  type="date"
                  value={form.appointment_date}
                  onChange={(v) => upd("appointment_date", v)}
                  testid="of-appointment-date"
                  icon={Calendar}
                />
              </div>
              <div>
                <Label>{T.appointment_time}</Label>
                <Input
                  type="time"
                  value={form.appointment_time}
                  onChange={(v) => upd("appointment_time", v)}
                  testid="of-appointment-time"
                  icon={Calendar}
                />
              </div>
            </div>
            <input
              ref={appointmentFileRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={onAppointmentFiles}
              style={{ display: "none" }}
              data-testid="of-appointment-file-input"
            />
            <button
              type="button"
              onClick={() => appointmentFileRef.current?.click()}
              style={{ ...css.uploadBtn, marginTop: 10 }}
              data-testid="of-appointment-upload-btn"
            >
              <Upload size={16} /> {T.add_appointment_doc}
            </button>
          </div>
        </section>
        )}

        {/* ── Documents (per-file custom titles) ── */}
        {step === 4 && (
        <section style={css.card}>
          <h2 style={css.h2}><FileText size={16} /> {T.section_docs}</h2>
          <p style={{ fontSize: 12, color: theme.textMuted, margin: "0 0 10px", lineHeight: 1.7 }}>
            {T.docs_hint}
          </p>
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple onChange={onFiles} style={{ display: "none" }} data-testid="of-file-input" />
          <button type="button" onClick={() => fileInputRef.current?.click()} style={css.uploadBtn} data-testid="of-upload-btn">
            <Upload size={16} /> {T.upload}
          </button>
          {files.length > 0 && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }} data-testid="of-files-list">
              {files.map((f, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 12px", background: theme.surface,
                  border: `1px solid ${theme.border}`, borderRadius: 10, fontSize: 12,
                }}>
                  <FileText size={16} color={theme.gold} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                    <input
                      type="text"
                      value={f.label}
                      onChange={(e) => relabelFile(i, e.target.value)}
                      placeholder={T.doc_title_ph}
                      data-testid={`of-file-title-${i}`}
                      style={{
                        background: "transparent", border: "none", outline: "none",
                        color: theme.text, fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                        borderBottom: `1px dashed ${theme.border}`, padding: "2px 0",
                      }}
                    />
                    <div style={{ display: "flex", gap: 8, alignItems: "center", color: theme.textMuted, fontSize: 11 }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
                        {f.file.name}
                      </span>
                      <span>{fmtNum(Math.round(f.file.size / 1024))} KB</span>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeFile(i)}
                    style={{ background: "transparent", border: "none", color: theme.errorText, cursor: "pointer" }}
                    data-testid={`of-remove-file-${i}`}>
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
        )}

        {/* ─── Step navigation ─── */}
        <div style={{
          display: "flex", gap: 10, marginTop: 12,
          justifyContent: "space-between", flexWrap: "wrap",
        }}>
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            style={{
              padding: "12px 20px", borderRadius: 12,
              background: "transparent", color: theme.text,
              border: `1px solid ${theme.border}`,
              fontWeight: 700, fontSize: 14, cursor: step === 1 ? "not-allowed" : "pointer",
              opacity: step === 1 ? 0.4 : 1, minHeight: 44, fontFamily: "inherit",
            }}
            data-testid="office-submit-prev"
          >
            {lang === "ar" ? "السابق" : "Previous"}
          </button>
          {step < totalSteps ? (
            <button
              type="button"
              onClick={() => {
                // Guard step 1 with the same rules as the backend so the
                // employee can't reach the final step (or the Save button)
                // holding invalid data. Errors clear when the user fixes them.
                if (step === 1) {
                  const name = (form.client_full_name || "").trim();
                  const mobile = (form.client_mobile_number || "").trim();
                  if (!name || !mobile) { setErr(T.err_required); return; }
                  if (name.length < 2) { setErr(T.err_name_short); return; }
                  if (mobile.length < 4) { setErr(T.err_mobile_short); return; }
                }
                setErr("");
                setStep((s) => Math.min(totalSteps, s + 1));
              }}
              style={{
                padding: "12px 20px", borderRadius: 12,
                background: theme.goldGradient, color: "#0A0F19",
                border: "none", fontWeight: 900, fontSize: 14, cursor: "pointer",
                minHeight: 44, fontFamily: "inherit",
              }}
              data-testid="office-submit-next"
            >
              {lang === "ar" ? "التالي" : "Next"}
            </button>
          ) : (
            <button onClick={submit} disabled={busy} style={css.submitBtn} data-testid="of-submit">
              {busy ? T.submitting : <><Send size={16} /> {T.submit}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Small bits ───
function Input({ value, onChange, placeholder, testid, icon: Icon, type = "text", inputMode }) {
  const { theme } = useI18n();
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10, padding: "10px 12px", marginBottom: 10, width: "100%" }}>
      {Icon && <Icon size={15} color={theme.textMuted} />}
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} inputMode={inputMode}
        data-testid={testid}
        style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: theme.text, fontSize: 14, fontFamily: "inherit", minWidth: 0 }}
      />
    </label>
  );
}
function Label({ children }) {
  const { theme } = useI18n();
  return <div style={{ fontSize: 12, fontWeight: 700, color: theme.textMuted, margin: "8px 0 6px" }}>{children}</div>;
}

function NumField({ label, value, onChange, testid, theme }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="number" min="0" step="0.01"
        value={value === 0 || value === "0" ? "" : value}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        placeholder="0"
        data-testid={testid}
        style={{
          width: "100%", padding: "10px 12px", borderRadius: 10,
          border: `1px solid ${theme.border}`, background: theme.surface,
          color: theme.text, fontSize: 14, fontFamily: "inherit", textAlign: "center",
          fontWeight: 700, fontVariantNumeric: "tabular-nums", boxSizing: "border-box",
        }}
      />
    </div>
  );
}

const makeStyles = (theme, dir) => ({
  backBtn: {
    background: "transparent", border: `1px solid ${theme.border}`,
    color: theme.text, padding: "7px 14px", borderRadius: 999,
    fontSize: 12, fontWeight: 700, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 6,
  },
  h1: { color: theme.gold, fontWeight: 900, fontSize: 24, margin: "14px 0 4px", display: "inline-flex", alignItems: "center", gap: 8 },
  sub: { fontSize: 13, color: theme.textMuted, margin: "0 0 18px", lineHeight: 1.7 },
  err: { background: theme.errorBg, color: theme.errorText, padding: 12, borderRadius: 10, marginBottom: 14, fontSize: 13 },
  card: { background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 18, marginBottom: 14 },
  h2: { color: theme.gold, fontSize: 14, fontWeight: 900, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 6 },
  grid2: { display: "grid", gridTemplateColumns: "1fr", gap: 0 },
  pillRow: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 4 },
  uploadBtn: {
    background: "transparent", border: `1.5px dashed ${theme.border}`, color: theme.text,
    padding: "12px 18px", borderRadius: 10, fontWeight: 700, fontSize: 13,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, width: "100%", justifyContent: "center",
  },
  submitBtn: {
    background: `linear-gradient(135deg, ${theme.gold}, #b8902a)`, color: "#0a0a0a",
    border: "none", padding: "14px 22px", borderRadius: 12, fontWeight: 900, fontSize: 15,
    cursor: "pointer", width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    boxShadow: "0 10px 24px rgba(212,175,55,0.3)",
  },
});
